import React, { memo, useState, useMemo } from "react";
import { Handle, Position, useNodeId } from "@xyflow/react";
import MaterialIcon from "../../BuilderComponents/MaterialIcon";

const handlePositions = [
  { id: "0", position: Position.Top, left: "50%", top: "0%" },
  { id: "1", position: Position.TopRight, left: "85%", top: "15%" },
  { id: "2", position: Position.Right, left: "100%", top: "50%" },
  { id: "3", position: Position.BottomRight, left: "85%", top: "85%" },
  { id: "4", position: Position.Bottom, left: "50%", top: "100%" },
  { id: "5", position: Position.BottomLeft, left: "15%", top: "85%" },
  { id: "6", position: Position.Left, left: "0%", top: "50%" },
  { id: "7", position: Position.TopLeft, left: "15%", top: "15%" }
];

const FormNode = memo(({ data, selected }) => {
  const { label, icon, backgroundColor = "black", borderColor = "#1e40af", textColor = "white", size = 100 } = data;

  const [hovered, setHovered] = useState(false);
  const nodeId = useNodeId();

  const handleStyle = useMemo(
    () => ({
      opacity: hovered ? 1 : 0,
      transition: "opacity 0.2s ease"
    }),
    [hovered]
  );

  const styles = useMemo(
    () => ({
      container: {
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "50%",
        background: backgroundColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: textColor,
        fontSize: "14px",
        fontWeight: "600",
        border: "3px solid rgba(255, 255, 255, 0.8)",
        boxShadow: selected
          ? `0 0 0 2px ${borderColor}, 0 8px 16px rgba(0,0,0,0.2), 0 0 16px ${borderColor}`
          : `0 0 0 2px gray, 8px 8px 24px 3px #D7D7D7`,
        position: "relative",
        overflow: "visible",
        transform: selected ? "scale(1.05)" : "scale(1)"
      },
      content: {
        position: "relative",
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "8px",
        textAlign: "center",
        maxWidth: "85%"
      },
      icon: {
        marginBottom: "6px",
        fontSize: "24px",
        opacity: 0.9
      }
    }),
    [size, backgroundColor, textColor, selected, borderColor]
  );

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="group"
      style={styles.container}
    >
      <div style={styles.content}>
        {icon && <MaterialIcon name={icon} style={styles.icon} />}
        <span style={{ lineHeight: 1.2 }}>{label}</span>
      </div>

      {/* Render Handles Dynamically */}
      {handlePositions.map(({ id, position, left, top }) => (
        <React.Fragment key={id}>
          <Handle
            type="source"
            position={position}
            id={position === Position.Bottom ? `${nodeId}-source` : `${nodeId}-source-${id}`}
            style={{ ...handleStyle, left, top }}
          />
          <Handle
            type="target"
            position={position}
            id={position === Position.Top ? `${nodeId}-target` : `${nodeId}-target-${id}`}
            style={{ ...handleStyle, left, top }}
          />
        </React.Fragment>
      ))}
    </div>
  );
});

FormNode.displayName = "CircleNode";

export default FormNode;
